import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Entity } from './entity';
import { ToastService } from './toast';
import { environment } from '../../../environments/environment';

/** Hôte autorisé pour les liens/QR d'invitation (= origine de l'app). */
const APP_HOST = new URL(environment.appOrigin).hostname;

/**
 * Réponse unique à un lien d'invitation valide qui n'ouvre pas un événement.
 *
 * Formulée sans repère de temps : l'application est un registre d'événements,
 * un tel lien n'y mène nulle part, et rien ne doit laisser croire à l'usager
 * qu'une mise à jour lui en ouvrira l'accès.
 */
export const NON_EVENT_INVITE_MESSAGE = "Ce lien d'invitation ne correspond à aucun événement.";

@Injectable({ providedIn: 'root' })
export class DeepLink {
  private router = inject(Router);
  private entity = inject(Entity);
  private toast = inject(ToastService);

  /**
   * Initialize deep link listener.
   * - Native (Android/iOS): intercepts App.appUrlOpen from Branch.io dynamic links
   * - Web: reads current URL query params on load (for PWA / browser share links)
   *
   * Expected URL format:
   *   https://transpa-event.web.app/join?eid=EVT-XXX&gid=GRP-YYY&tok=TOKEN
   */
  init(): void {
    if (Capacitor.isNativePlatform()) {
      // Native deep link via Capacitor App plugin
      App.addListener('appUrlOpen', ({ url }) => {
        void this.handleUrl(url);
      });
    } else {
      // Web PWA: only process if URL contains an invitation marker to avoid
      // navigating away on every app load (e.g. bookmarks with eid= params).
      // `eid=` → event group invite ; `token=` → entity (church/asso) invite.
      const href = window.location.href;
      if (href.includes('eid=') || href.includes('eventId=') || href.includes('token=')) {
        // Clear the params from the address bar only AFTER the navigation has been
        // dispatched, so the reload-guard cleanup can't race the async navigate().
        void this.handleUrl(href).finally(() => {
          history.replaceState({}, '', window.location.pathname);
        });
      }
    }
  }

  /**
   * Traite une URL d'invitation (deep-link natif/web OU QR scanné) et route en
   * conséquence. Retourne `true` si l'URL a été reconnue et une navigation
   * déclenchée, `false` sinon (URL hors /join ou paramètres absents).
   */
  async handleUrl(url: string): Promise<boolean> {
    try {
      const parsed = new URL(url);

      // Sécurité : n'accepter QUE l'origine de l'app et le chemin exact /join — un
      // lien profond / QR scanné est une entrée non fiable. Tout autre hôte ou
      // chemin est ignoré (aucune navigation, aucun appel réseau déclenché).
      if (parsed.hostname !== APP_HOST) return false;
      if (parsed.pathname !== '/join') return false;

      // Event invite — accept BOTH the native short keys (eid/gid/tok) and the web
      // long keys (eventId/groupId/token). Native deep links use the short form;
      // the event-create share link and the web /join route use the long form. An
      // event link therefore routes to /join in either shape, and ONLY a link with
      // no event id at all falls through to the entity (church/asso) branch below —
      // which prevents an event `token=` link from being misread as an entity invite.
      const eventId = parsed.searchParams.get('eid') ?? parsed.searchParams.get('eventId');
      const groupId = parsed.searchParams.get('gid') ?? parsed.searchParams.get('groupId');
      const eventToken = parsed.searchParams.get('tok') ?? parsed.searchParams.get('token');

      // Entity invitation (church/association): /join?token=<inviteTokenGeneral>&r=<role>
      const entityToken = parsed.searchParams.get('token');

      if (eventId) {
        await this.router.navigate(['/join'], {
          queryParams: { eventId, groupId, token: eventToken },
        });
        return true;
      }
      if (entityToken) {
        // No event id → entity invite. Resolve the token server-side (which also
        // enrols the member) then navigate to the entity's space so the link works.
        return await this.handleEntityToken(entityToken);
      }
      return false;
    } catch {
      // Not a valid URL — ignore
      return false;
    }
  }

  /**
   * Résout un token d'invitation d'entité (église/association) puis oriente.
   *
   * Le token reste résolu côté serveur — l'inscription du membre est donc bien
   * enregistrée — mais les espaces d'entités sont hors périmètre de l'alpha et
   * leurs routes ont été retirées (voir docs/adr/0001-registre-declaratif-alpha.md).
   * On replie sur l'accueil avec un message, plutôt que de naviguer vers une
   * route inexistante qui laisserait le lien d'invitation dans le vide.
   */
  private async handleEntityToken(entityToken: string): Promise<boolean> {
    try {
      const resolved = await this.entity.resolveJoinToken(entityToken);
      if (!resolved) return false;
      await this.router.navigate(['/home']);
      this.toast.neutral(NON_EVENT_INVITE_MESSAGE);
      return true;
    } catch {
      // Token invalid/expired — ignore (no navigation).
      return false;
    }
  }
}
